import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrench, Briefcase, ArrowRight, Sparkles, Zap, Globe, Shield, TrendingUp, Users, Star, Award } from 'lucide-react';

const MarketingHome = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section - Completely Different Design */}
      <section className="relative overflow-hidden">
        {/* Animated Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500">
          <div className="absolute inset-0 bg-grid-white/10 bg-[size:50px_50px]"></div>
        </div>
        
        {/* Floating Shapes */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-pink-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>

        <div className="container mx-auto px-4 py-24 md:py-32 relative z-10">
          <div className="max-w-5xl mx-auto">
            {/* Main Content - Split Layout */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Side - Text */}
              <div className="text-white">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-md rounded-full text-sm font-semibold mb-6 border border-white/30">
                  <Sparkles className="w-4 h-4" />
                  <span>Australia's Dual Marketplace</span>
                </div>
                
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight drop-shadow-lg">
                  One Platform.
                  <br />
                  <span className="text-blue-200">Three Solutions.</span>
                </h1>
                
                <p className="text-xl md:text-2xl text-blue-50 mb-8 leading-relaxed">
                  Find tradies, discover jobs, and showcase your talent - Australia's complete professional marketplace.
                </p>

                <div className="flex flex-wrap gap-4 mb-8">
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                    <Users className="w-5 h-5" />
                    <span className="text-sm font-medium">10,000+ Active Users</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                    <Star className="w-5 h-5 fill-current" />
                    <span className="text-sm font-medium">4.8/5 Rating</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                    <Shield className="w-5 h-5" />
                    <span className="text-sm font-medium">100% Verified</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => navigate('/register')}
                    className="px-8 py-4 bg-white text-blue-600 font-bold rounded-xl hover:bg-blue-50 transition-all shadow-2xl flex items-center justify-center gap-2 text-lg"
                  >
                    Get Started Free
                    <ArrowRight className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => navigate('/tradies/browse')}
                    className="px-8 py-4 bg-transparent border-2 border-white text-white font-bold rounded-xl hover:bg-white hover:text-blue-600 transition-all text-lg"
                  >
                    Explore Now
                  </button>
                </div>
              </div>

              {/* Right Side - Product Cards Stack */}
              <div className="space-y-4">
                {/* GoTradies Card */}
                <div 
                  onClick={() => navigate('/tradies')}
                  className="group cursor-pointer bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-white/50 dark:border-gray-700"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <Wrench className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                        GoTradies
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300 mb-3">
                        Connect with verified tradies. Get quotes, compare, and hire with confidence.
                      </p>
                      <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 font-semibold">
                        <span>Explore GoTradies</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* GoJobs Card */}
                <div 
                  onClick={() => navigate('/jobs')}
                  className="group cursor-pointer bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-white/50 dark:border-gray-700"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <Briefcase className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                          GoJobs
                        </h3>
                        <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 text-xs font-bold rounded-full">Beta</span>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 mb-3">
                        Find jobs or hire talent with video applications and smart matching.
                      </p>
                      <div className="flex items-center gap-2 text-sm text-purple-600 dark:text-purple-400 font-semibold">
                        <span>Explore GoJobs</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* GoTalent Card */}
                <div 
                  onClick={() => navigate('/talent')}
                  className="group cursor-pointer bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-white/50 dark:border-gray-700"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <Award className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors">
                          GoTalent
                        </h3>
                        <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-xs font-bold rounded-full">Beta</span>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 mb-3">
                        Turn your profile into opportunities: video intro, certificates, and direct employer offers.
                      </p>
                      <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 font-semibold">
                        <span>Explore GoTalent</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-12 bg-background border-y">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto text-center">
            <div>
              <div className="text-4xl font-bold text-foreground mb-1">100+</div>
              <div className="text-sm text-muted-foreground">Verified Tradies</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-foreground mb-1">1,000+</div>
              <div className="text-sm text-muted-foreground">Jobs Completed</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-foreground mb-1">4.8★</div>
              <div className="text-sm text-muted-foreground">Average Rating</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-foreground mb-1">24/7</div>
              <div className="text-sm text-muted-foreground">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid - Unique Layout */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">Everything You Need in One Place</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Powerful features for homeowners, tradies, job seekers, and employers
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {/* Feature 1 */}
              <div className="bg-card rounded-2xl p-6 border border-border hover:border-blue-300 dark:hover:border-blue-600 transition-all hover:shadow-lg">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-foreground">Verified Professionals</h3>
                <p className="text-muted-foreground text-sm">
                  Every tradie and employer verified with credentials, licenses, and background checks
                </p>
              </div>

              {/* Feature 2 */}
              <div className="bg-card rounded-2xl p-6 border border-border hover:border-purple-300 dark:hover:border-purple-600 transition-all hover:shadow-lg">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-foreground">Instant Connections</h3>
                <p className="text-muted-foreground text-sm">
                  Message directly, get quotes fast, or apply to jobs with one click. No waiting around.
                </p>
              </div>

              {/* Feature 3 */}
              <div className="bg-card rounded-2xl p-6 border border-border hover:border-green-300 dark:hover:border-green-600 transition-all hover:shadow-lg">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mb-4">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-foreground">Grow Your Business</h3>
                <p className="text-muted-foreground text-sm">
                  Tradies and employers can showcase work, manage leads, and scale efficiently
                </p>
              </div>

              {/* Feature 4 */}
              <div className="bg-card rounded-2xl p-6 border border-border hover:border-yellow-300 dark:hover:border-yellow-600 transition-all hover:shadow-lg">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center mb-4">
                  <Star className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-foreground">Real Reviews</h3>
                <p className="text-muted-foreground text-sm">
                  Authentic reviews from verified users help you make informed decisions
                </p>
              </div>

              {/* Feature 5 */}
              <div className="bg-card rounded-2xl p-6 border border-border hover:border-pink-300 dark:hover:border-pink-600 transition-all hover:shadow-lg">
                <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl flex items-center justify-center mb-4">
                  <Globe className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-foreground">Australia-Wide</h3>
                <p className="text-muted-foreground text-sm">
                  Covering all major cities and regions across Australia with local expertise
                </p>
              </div>

              {/* Feature 6 */}
              <div className="bg-card rounded-2xl p-6 border border-border hover:border-cyan-300 dark:hover:border-cyan-600 transition-all hover:shadow-lg">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl flex items-center justify-center mb-4">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-foreground">Smart Technology</h3>
                <p className="text-muted-foreground text-sm">
                  AI-powered matching, video interviews, and automated workflows save you time
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Showcase - Side by Side Comparison */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">Choose Your Platform</h2>
              <p className="text-xl text-muted-foreground">
                One account gives you access to both. Start with one, add the other anytime.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {/* GoTradies Detailed Card */}
              <div className="group relative bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 rounded-3xl p-8 border-2 border-blue-200 dark:border-blue-800 overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-blue-400 rounded-full filter blur-3xl opacity-20"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-xl">
                      <Wrench className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold text-foreground">GoTradies</h3>
                      <p className="text-sm text-muted-foreground">For Home Services</p>
                    </div>
                  </div>

                  <p className="text-muted-foreground mb-6 text-lg">
                    Australia's most trusted marketplace for finding and hiring quality tradespeople.
                  </p>

                  <div className="space-y-3 mb-8">
                    <div className="flex items-center gap-3 text-card-foreground">
                      <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <span className="text-sm">Browse 100+ verified tradie profiles</span>
                    </div>
                    <div className="flex items-center gap-3 text-card-foreground">
                      <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <span className="text-sm">Get instant quotes and compare</span>
                    </div>
                    <div className="flex items-center gap-3 text-card-foreground">
                      <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <span className="text-sm">Read real verified reviews</span>
                    </div>
                    <div className="flex items-center gap-3 text-card-foreground">
                      <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <span className="text-sm">Secure messaging & payments</span>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate('/tradies')}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold py-4 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg flex items-center justify-center gap-2 group-hover:shadow-xl"
                  >
                    <span>Explore GoTradies</span>
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* GoJobs Detailed Card */}
              <div className="group relative bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900 rounded-3xl p-8 border-2 border-purple-200 dark:border-purple-800 overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-purple-400 rounded-full filter blur-3xl opacity-20"></div>
                
                {/* Coming Soon Badge */}
                <div className="absolute top-6 right-6 px-3 py-1 bg-purple-500 text-white text-xs font-bold rounded-full shadow-lg">
                  Beta
                </div>

                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-xl">
                      <Briefcase className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold text-foreground">GoJobs</h3>
                      <p className="text-sm text-muted-foreground">For Career Growth</p>
                    </div>
                  </div>

                  <p className="text-muted-foreground mb-6 text-lg">
                    Next-gen job marketplace with video applications and one-way interviews.
                  </p>

                  <div className="space-y-3 mb-8">
                    <div className="flex items-center gap-3 text-card-foreground">
                      <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <span className="text-sm">Video cover letters & applications</span>
                    </div>
                    <div className="flex items-center gap-3 text-card-foreground">
                      <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <span className="text-sm">One-way video interviews</span>
                    </div>
                    <div className="flex items-center gap-3 text-card-foreground">
                      <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <span className="text-sm">AI-powered job matching</span>
                    </div>
                    <div className="flex items-center gap-3 text-card-foreground">
                      <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <span className="text-sm">Track applications in real-time</span>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate('/jobs')}
                    className="w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white font-bold py-4 rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all shadow-lg flex items-center justify-center gap-2 group-hover:shadow-xl"
                  >
                    <span>Explore GoJobs</span>
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* GoTalent Card */}
              <div className="group relative bg-gradient-to-br from-green-50 to-teal-50 dark:from-green-950 dark:to-teal-950 rounded-3xl p-8 border-2 border-green-200 dark:border-green-800 overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-green-400 rounded-full filter blur-3xl opacity-20"></div>

                {/* Coming Soon Badge */}
                <div className="absolute top-6 right-6 px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full shadow-lg">
                  Beta
                </div>

                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-xl">
                      <Award className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold text-foreground">GoTalent</h3>
                      <p className="text-sm text-muted-foreground">Candidate Marketplace</p>
                    </div>
                  </div>

                  <p className="text-muted-foreground mb-6 text-lg">
                    Let employers discover you or search top candidates for your roles.
                  </p>

                  <div className="space-y-3 mb-8">
                    <div className="flex items-center gap-3 text-card-foreground">
                      <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <span className="text-sm">Create discoverable profile</span>
                    </div>
                    <div className="flex items-center gap-3 text-card-foreground">
                      <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <span className="text-sm">Receive job offers directly</span>
                    </div>
                    <div className="flex items-center gap-3 text-card-foreground">
                      <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <span className="text-sm">Employers search candidates</span>
                    </div>
                    <div className="flex items-center gap-3 text-card-foreground">
                      <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <span className="text-sm">Privacy protected (contact hidden)</span>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate('/talent')}
                    className="w-full bg-gradient-to-r from-green-600 to-teal-600 text-white font-bold py-4 rounded-xl hover:from-green-700 hover:to-teal-700 transition-all shadow-lg flex items-center justify-center gap-2 group-hover:shadow-xl"
                  >
                    <span>Explore GoTalent</span>
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Unique Horizontal Flow */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">Simple. Fast. Secure.</h2>
              <p className="text-xl text-muted-foreground">
                Get started in three easy steps
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 relative">
              {/* Step 1 */}
              <div className="relative">
                <div className="bg-card rounded-2xl p-8 border-2 border-border text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6 shadow-lg">
                    1
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-foreground">Create Account</h3>
                  <p className="text-muted-foreground">
                    Sign up once and access both GoTradies and GoJobs with one login
                  </p>
                </div>
                {/* Arrow */}
                <div className="hidden md:block absolute top-1/2 -right-8 transform -translate-y-1/2">
                  <ArrowRight className="w-8 h-8 text-muted-foreground" />
                </div>
              </div>

              {/* Step 2 */}
              <div className="relative">
                <div className="bg-card rounded-2xl p-8 border-2 border-border text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6 shadow-lg">
                    2
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-foreground">Choose Platform</h3>
                  <p className="text-muted-foreground">
                    Select GoTradies for home services or GoJobs for employment needs
                  </p>
                </div>
                {/* Arrow */}
                <div className="hidden md:block absolute top-1/2 -right-8 transform -translate-y-1/2">
                  <ArrowRight className="w-8 h-8 text-muted-foreground" />
                </div>
              </div>

              {/* Step 3 */}
              <div>
                <div className="bg-card rounded-2xl p-8 border-2 border-border text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6 shadow-lg">
                    3
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-foreground">Get Results</h3>
                  <p className="text-muted-foreground">
                    Hire quality tradies or land your dream job. Start achieving your goals
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Unique Split Design */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 rounded-3xl p-12 text-center text-white shadow-2xl relative overflow-hidden">
              {/* Background Effect */}
              <div className="absolute inset-0 bg-grid-white/10 bg-[size:30px_30px]"></div>
              
              <div className="relative z-10">
                <Sparkles className="w-16 h-16 mx-auto mb-6" />
                <h2 className="text-4xl md:text-5xl font-bold mb-6 drop-shadow-lg">
                  Ready to Transform Your Future?
                </h2>
                <p className="text-xl text-blue-50 mb-8 max-w-2xl mx-auto drop-shadow">
                  Join thousands of Australians who trust GoFella for their service and career needs
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => navigate('/register')}
                    className="px-8 py-4 bg-white text-blue-600 font-bold rounded-xl hover:bg-blue-50 transition-all shadow-xl text-lg"
                  >
                    Sign Up Free Now
                  </button>
                  <button
                    onClick={() => navigate('/tradies/browse')}
                    className="px-8 py-4 bg-transparent border-2 border-white text-white font-bold rounded-xl hover:bg-white hover:text-purple-600 transition-all text-lg"
                  >
                    Browse Tradies
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MarketingHome;
